import { Splash } from 'core/styles/Banner'
import Link from 'next/link'
import React from 'react'
import styled, { css } from 'styled-components'
import { lighter } from '../helpers/color'
import { breakpoint } from '../layout/styles'
import { Container } from '../styles/Container'
import { Grid, Cell } from '../styles/Grid'

// Helper to only include the <Root> element if it's needed, ie. if
// we're using a black background. The Wrapper has vertical margins,
// so I don't know if there's any guarantee of backwards compatibility
// if we now wrap that in another element.
const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const PhotoSections = ({ data }: any) =>
  <ConditionalWrapper
    condition={data.black_background}
    wrapper={(children: any) => <Root black={data.black_background}>{children}</Root>}>
    <Wrapper background={lighter(data.background_color?.[0]?.background)}>
      <SectionTitle large={data.large_title}>{data.title}</SectionTitle>
      <SectionDescription>{data.description}</SectionDescription>
      <Grid>
        {data.items.map((item: any) =>
          <Section key={item._metadata.uid}>
            <img src={item.icon?.url} />
            <h4>{item.title}</h4>
            <p>{item.description}</p>
            {item.cta && item.cta.href &&
              <Link href={item.cta.href}>
                <Cta>{item.cta.title}</Cta>
              </Link>}
          </Section>)}
      </Grid>
    </Wrapper>
    {data.disclaimer_text && (
      <Disclaimer>
        {data.disclaimer_text}
      </Disclaimer>
    )}
  </ConditionalWrapper>

export default PhotoSections

const Root = styled.div<{ black : boolean }>`
  // make background black and adjust vertical spacing
  ${(props) => props.black && css`
    padding: 3.5rem 0 2.5rem 0;
    border-bottom: 1px solid var(--white);
    background: black !important;
    @media ${breakpoint.desktop} {
      padding: 6rem 0 4.75rem 0;
    }
    h1, h2, h3, h4, h5, h6, p, li{
      color: var(--white);
      a{
        color: var(--white);
        text-decoration: underline;
        font-weight: 600;
      }
    }
    ${Wrapper}{
      margin-top: 0;
      margin-bottom: 0;
    }
    ${SectionTitle}{
      margin-top: 0;
    }
    // add padding top for black background only since we remove
    // margin bottom from Wrapper.
    ${Disclaimer}{
      color: var(--white);
      padding-top: 1.5rem;
      @media ${breakpoint.desktop} {
        margin-bottom: -2rem;
        padding-top: 3rem;
      }
    }
  `}
`

const Wrapper = styled(Container)`
  color: var(--text);
  margin: 36px 20px;
  background: ${props => props.background || 'none'};
  padding: 20px;
  @media ${breakpoint.desktop} {
    margin: 48px auto 48px;
    padding: 0;
    background: none;
    max-width: 920px;
  }
`

export const Section = styled(Cell)`
  text-align: center; 
  margin: 16px 0;
  @media ${breakpoint.desktop} {
    width: 33.3333%;
    margin: 16px;
  }
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
  img {
    display: block;
    margin: 0 auto;
  }
  h4 {
    font-size: 16px;
    font-style: normal;
    font-weight: bold;
    text-align: center;
  }
  p {
    font-size: 14px;
    @media ${breakpoint.desktop} {
      font-size: 16px;
    }
  }
  img {
    width: 100%;
    height: 100%;
    max-width: 287px;
    max-height: 185px;
    object-fit: cover;
  }
`
const SectionTitle = styled.h3<{large: string}>`
  margin: 16px 0;
  text-align: center;
  font-style: normal;
  font-weight: 700;
  font-size: ${p => p.large ? '32px' : '24px'};
  @media ${breakpoint.desktop} {
    margin: 48px auto 0;
    font-size: ${p => p.large ? '38px' : '26px'};
  }
`

const SectionDescription = styled.p`
  @media ${breakpoint.desktop} {
    margin: 30px auto;
    text-align: center;
  }
`

const Cta = styled.a`
  display: inline-block;
  cursor: pointer;
  background: #000;
  color: white;
  text-transform: uppercase;
  padding: 10px 20px;
  margin-top: 16px;
`

const Disclaimer = styled.p`
  color: var(--text);
  width: 100%;
  max-width: 57rem;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  margin: 0 auto;
  font-size: 0.625rem;
  line-height: 105%;
  text-align: center;
  @media ${breakpoint.desktop} {
    font-size: 0.75rem;
  }
`